class FaizProductPage extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();
    this.bindGallery();
    this.bindQuantity();
    this.bindOptions();
    this.bindZoom();
    this.bindViewerCounter();
  }

  readVariants() {
    const node = this.querySelector('[data-faiz-variants]');
    if (!node) return [];
    try { return JSON.parse(node.textContent); } catch (error) { return []; }
  }

  bindGallery() {
    const thumbs = Array.from(this.querySelectorAll('[data-faiz-thumb]'));
    const media = this.querySelectorAll('[data-faiz-media]');
    const mainMedia = this.querySelector('.faiz-pdp__main-media');
    let swipeAnimation = '';

    const activate = (thumb) => {
      const id = thumb.dataset.faizThumb;
      thumbs.forEach((item) => {
        const active = item === thumb;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
      media.forEach((item) => {
        const active = item.dataset.faizMedia === id;
        item.classList.remove('is-swipe-next', 'is-swipe-prev');
        item.classList.toggle('is-active', active);
        item.toggleAttribute('hidden', !active);
        if (active && swipeAnimation) {
          item.classList.add(swipeAnimation);
          item.addEventListener('animationend', () => item.classList.remove('is-swipe-next', 'is-swipe-prev'), { once: true });
        }
        const video = item.querySelector('video');
        if (video && !active) video.pause();
      });
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      const activeImage = this.querySelector(`[data-faiz-media="${CSS.escape(id)}"] img`);
      this.updateZoom(activeImage);
      swipeAnimation = '';
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => activate(thumb)));

    if (!mainMedia || thumbs.length < 2) return;
    let touchStartX = 0;
    let touchStartY = 0;

    mainMedia.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    mainMedia.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      const activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      const direction = deltaX < 0 ? 1 : -1;
      const targetIndex = (activeIndex + direction + thumbs.length) % thumbs.length;
      swipeAnimation = direction > 0 ? 'is-swipe-next' : 'is-swipe-prev';
      thumbs[targetIndex].click();
    }, { passive: true });
  }

  bindQuantity() {
    this.querySelectorAll('[data-faiz-qty]').forEach((wrapper) => {
      const input = wrapper.querySelector('input');
      wrapper.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const max = Number(input.max) || Infinity;
        const next = Number(input.value || min) + (button.dataset.action === 'increase' ? step : -step);
        input.value = Math.max(min, Math.min(max, next));
        this.querySelectorAll('input[name="quantity"]').forEach((field) => { field.value = input.value; });
      }));
    });
  }

  bindOptions() {
    if (!this.variants.length) return;
    this.querySelectorAll('[data-faiz-option]').forEach((select) => {
      select.addEventListener('change', () => this.selectVariant());
    });
  }

  selectVariant() {
    const selected = Array.from(this.querySelectorAll('[data-faiz-option]')).map((select) => select.value);
    const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
    if (!variant) return;
    this.querySelectorAll('input[name="id"]').forEach((input) => { input.value = variant.id; });
    this.querySelectorAll('[data-faiz-price]').forEach((node) => { node.textContent = this.formatMoney(variant.price); });
    this.querySelectorAll('[data-faiz-compare]').forEach((node) => {
      const visible = variant.compare_at_price && variant.compare_at_price > variant.price;
      node.hidden = !visible;
      node.textContent = visible ? this.formatMoney(variant.compare_at_price) : '';
    });
    this.querySelectorAll('[data-faiz-saving]').forEach((node) => {
      const visible = variant.compare_at_price && variant.compare_at_price > variant.price;
      node.hidden = !visible;
      const percent = visible ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
      node.textContent = visible ? `Économisez ${percent} %` : '';
    });
    this.querySelectorAll('[data-faiz-atc]').forEach((button) => {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.availableText : button.dataset.soldoutText;
    });
    const inventory = this.querySelector('[data-faiz-inventory]');
    if (inventory) {
      inventory.classList.toggle('is-out', !variant.available);
      inventory.classList.remove('is-low');
      const label = inventory.querySelector('[data-faiz-inventory-label]');
      if (label) label.textContent = variant.available ? 'Disponible' : 'Épuisé';
    }
    if (variant.featured_media) {
      const thumb = this.querySelector(`[data-faiz-thumb="${CSS.escape(String(variant.featured_media.id))}"]`);
      if (thumb) thumb.click();
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency }).format(cents / 100);
  }

  bindZoom() {
    const trigger = this.querySelector('[data-faiz-zoom-trigger]');
    const dialog = this.querySelector('[data-faiz-zoom-dialog]');
    if (!trigger || !dialog || typeof dialog.showModal !== 'function') return;
    trigger.addEventListener('click', () => {
      const activeImage = this.querySelector('[data-faiz-media].is-active img');
      this.updateZoom(activeImage);
      dialog.showModal();
    });
    dialog.querySelector('[data-faiz-zoom-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
  }

  bindViewerCounter() {
    const counter = this.querySelector('[data-faiz-viewer]');
    const text = counter?.querySelector('[data-faiz-viewer-text]');
    if (!counter || !text) return;
    const minimum = Number(counter.dataset.min) || 2;
    const maximum = Math.max(minimum, Number(counter.dataset.max) || 13);
    let current = 3;

    const render = () => {
      const noun = current === 1 ? 'personne regarde' : 'personnes regardent';
      text.textContent = `${current} ${noun} actuellement cet article`;
    };
    const chooseNext = () => {
      let next = current;
      while (next === current && maximum > minimum) {
        next = Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;
      }
      current = next;
      render();
      window.setTimeout(chooseNext, 22000 + Math.floor(Math.random() * 17000));
    };

    current = Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;
    render();
    window.setTimeout(chooseNext, 24000 + Math.floor(Math.random() * 12000));
  }

  updateZoom(image) {
    const dialogImage = this.querySelector('[data-faiz-zoom-image]');
    if (!dialogImage || !image) return;
    dialogImage.src = image.dataset.zoomSrc || image.currentSrc || image.src;
    dialogImage.alt = image.alt || '';
  }
}

if (!customElements.get('faiz-product-page')) customElements.define('faiz-product-page', FaizProductPage);
